// ABOUTME: Overbooking Allowance form — editable table with customer tier, overbooking %, lead time, UoM, and reference event.
// ABOUTME: Uses react-hook-form; "Add customer tier" button appends rows; delete icon removes them.
import {Button, ControlledInput, Icon, TextMedium} from '@hm/fundamental'
import {CountryConfigsTooltip} from './CountryConfigsTooltip'
import {mdiInformationOutline, mdiPlus, mdiTrashCanOutline} from '@mdi/js'
import React, {useEffect, useMemo, useRef} from 'react'
import {useForm, useWatch} from 'react-hook-form'

import {useTranslation} from '../../../../hooks/useTranslation'
import {useRowTouchTracking} from '../utils'
import {
  OverbookingAllowanceConfig,
  OverbookingAllowanceEntryFormValues,
  OverbookingAllowanceFormValues,
} from '../types/overbookingAllowance'
import {FilterDropdown} from './FilterDropdown'
import {NumericalInput} from './NumericalInput'

export interface OverbookingAllowanceFormProps {
  config: OverbookingAllowanceConfig
  isReadOnly: boolean
  onDirtyChange: (dirty: boolean) => void
  onValidChange: (valid: boolean) => void
  onValuesChange?: (values: OverbookingAllowanceFormValues) => void
  /** Backend-provided UoM options for the lead time dropdown */
  uomOptions: {value: string; label: string}[]
  /** Backend-provided reference event options for the reference event dropdown */
  referenceEventOptions: {value: string; label: string}[]
  /** Country-default (root) scope: at least one entry is required (no parent to inherit from). */
  isRoot?: boolean
  'data-cy'?: string
}

const EMPTY_ENTRY: OverbookingAllowanceEntryFormValues = {
  customerTier: '',
  overbookingPercent: 0,
  leadTimeValue: 0,
  leadTimeUomId: 'businessDay',
  leadTimeReferenceEvent: '',
}

const toFormValues = (cfg: OverbookingAllowanceConfig): OverbookingAllowanceFormValues => ({
  entries: cfg.entries.map((e) => ({...e, leadTimeUomId: 'businessDay'})),
})

interface EntryErrors {
  customerTier?: string
  overbookingPercent?: string
  leadTimeValue?: string
  leadTimeUomId?: string
  leadTimeReferenceEvent?: string
}

const getAllowanceCombinationKey = (entry: OverbookingAllowanceEntryFormValues): string | undefined => {
  const customerTier = entry.customerTier?.trim()
  const leadTimeUomId = entry.leadTimeUomId?.trim()
  const leadTimeReferenceEvent = entry.leadTimeReferenceEvent?.trim()

  if (!customerTier || !Number.isFinite(entry.leadTimeValue) || !leadTimeUomId || !leadTimeReferenceEvent) {
    return undefined
  }

  return JSON.stringify([
    customerTier.toLowerCase(),
    entry.leadTimeValue,
    leadTimeUomId.toLowerCase(),
    leadTimeReferenceEvent.toLowerCase(),
  ])
}

const COLUMN_HEADERS: {labelKey: string; tooltipKey?: string; className: string}[] = [
  {
    labelKey: 'countryConfigurations.overbookingAllowance.customerTier',
    tooltipKey: 'countryConfigurations.overbookingAllowance.customerTierTooltip',
    className: 'w-[133px] shrink-0',
  },
  {
    labelKey: 'countryConfigurations.overbookingAllowance.overbookingPercent',
    tooltipKey: 'countryConfigurations.overbookingAllowance.overbookingPercentTooltip',
    className: 'w-[148px] shrink-0',
  },
  {
    labelKey: 'countryConfigurations.overbookingAllowance.leadTime',
    tooltipKey: 'countryConfigurations.overbookingAllowance.leadTimeTooltip',
    className: 'w-[140px] shrink-0',
  },
  {
    labelKey: 'countryConfigurations.overbookingAllowance.unitOfMeasure',
    className: 'w-[188px] shrink-0',
  },
  {
    labelKey: 'countryConfigurations.overbookingAllowance.referenceEvent',
    tooltipKey: 'countryConfigurations.overbookingAllowance.referenceEventTooltip',
    className: 'flex-1 min-w-0',
  },
  {
    labelKey: 'countryConfigurations.overbookingAllowance.actions',
    className: 'w-[66px] shrink-0',
  },
]

export const OverbookingAllowanceForm: React.FC<OverbookingAllowanceFormProps> = ({
  config,
  isReadOnly,
  onDirtyChange,
  onValidChange,
  onValuesChange,
  uomOptions,
  referenceEventOptions,
  isRoot = false,
  'data-cy': dataCy,
}) => {
  const {t} = useTranslation()
  const rowIdCounter = useRef(0)
  const nextRowIdForInit = () => String(rowIdCounter.current++)
  const formDefaultValues = useMemo(
    () => ({entries: toFormValues(config).entries.map((e) => ({...e, _rowId: nextRowIdForInit()}))}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config],
  )
  const {control, setValue, reset, formState: {isDirty}} = useForm<OverbookingAllowanceFormValues>({
    defaultValues: formDefaultValues,
  })

  useEffect(() => {
    reset(formDefaultValues)
  }, [formDefaultValues, reset])

  const entries = useWatch({control, name: 'entries'}) ?? []

  const errors = useMemo<EntryErrors[]>(() => {
    const combinationCounts = new Map<string, number[]>()
    entries.forEach((entry, i) => {
      const key = getAllowanceCombinationKey(entry)
      if (key) {
        const indices = combinationCounts.get(key) ?? []
        indices.push(i)
        combinationCounts.set(key, indices)
      }
    })

    return entries.map((entry) => {
      const errs: EntryErrors = {}
      if (!entry.customerTier.trim()) {
        errs.customerTier = t('countryConfigurations.overbookingAllowance.validation.required')
      } else if (entry.customerTier.trim().length > 100) {
        errs.customerTier = t('countryConfigurations.overbookingAllowance.validation.maxLength')
      } else {
        const key = getAllowanceCombinationKey(entry)
        const indices = key ? combinationCounts.get(key) : undefined
        if (indices && indices.length > 1) {
          errs.customerTier = t('countryConfigurations.overbookingAllowance.validation.duplicateTier')
        }
      }
      if (entry.overbookingPercent < 0) {
        errs.overbookingPercent = t('countryConfigurations.overbookingAllowance.validation.nonNegative')
      }
      if (entry.leadTimeValue < 0) {
        errs.leadTimeValue = t('countryConfigurations.overbookingAllowance.validation.nonNegative')
      }
      if (!entry.leadTimeUomId) {
        errs.leadTimeUomId = t('countryConfigurations.overbookingAllowance.validation.required')
      }
      if (!entry.leadTimeReferenceEvent) {
        errs.leadTimeReferenceEvent = t('countryConfigurations.overbookingAllowance.validation.required')
      }
      return errs
    })
  }, [entries, t])

  const hasEntryErrors = !errors.every((e) => Object.keys(e).length === 0)
  const isEmptyOverride = entries.length === 0 && isDirty && config._meta.isInherited
  const isEmptyRoot = isRoot && entries.length === 0
  const isValid = !hasEntryErrors && !isEmptyOverride && !isEmptyRoot

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    onValidChange(isValid)
  }, [isValid, onValidChange])

  useEffect(() => {
    onValuesChange?.({entries})
  }, [entries, onValuesChange])

  const entriesLength = entries.length
  const {touchedRows, markTouched, removeTouched, newRowRef, nextRowId, markAdded} = useRowTouchTracking(entriesLength, config.entries.length)

  const handleAddEntry = () => {
    markAdded()
    setValue('entries', [...entries, {...EMPTY_ENTRY, _rowId: nextRowId()}], {shouldDirty: true})
  }

  const handleRemoveEntry = (index: number) => {
    const removedId = entries[index]?._rowId
    setValue('entries', entries.filter((_, i) => i !== index), {shouldDirty: true})
    if (removedId) removeTouched(removedId)
  }

  const handleEntryChange = <K extends keyof OverbookingAllowanceEntryFormValues>(
    index: number,
    field: K,
    value: OverbookingAllowanceEntryFormValues[K],
  ) => {
    const newEntries = entries.map((entry, i) =>
      i === index ? {...entry, [field]: value} : entry,
    )
    setValue('entries', newEntries, {shouldDirty: true})

    if (['customerTier', 'leadTimeValue', 'leadTimeUomId', 'leadTimeReferenceEvent'].includes(field)) {
      markTouched(entries[index]?._rowId ?? String(index))
    }
  }

  return (
    <div className="flex flex-col gap-4 p-6 w-full" data-cy={dataCy ?? 'overbooking-allowance-form'}>
      {/* Add button */}
      {!isReadOnly && (
        <Button
          variant="secondary"
          size="small"
          iconPrefix={mdiPlus}
          onClick={handleAddEntry}
          data-cy="overbooking-add-entry"
          className="w-full"
        >
          {t('countryConfigurations.overbookingAllowance.addCustomerTier')}
        </Button>
      )}
      {isEmptyRoot && (
        <p role="alert" className="text-xs leading-4 text-negative" data-cy="overbooking-root-empty">
          {t('countryConfigurations.overbookingAllowance.validation.atLeastOneEntry')}
        </p>
      )}

      {/* Table */}
      {entries.length > 0 && (
        <div className="w-full relative z-10">
          {/* Column headers */}
          <div className="flex bg-columnheader_background rounded-t-md">
            {COLUMN_HEADERS.map((col) => (
              <div
                key={col.labelKey}
                className={`${col.className} px-2 py-2 flex items-center gap-1`}
              >
                <TextMedium className="text-foreground_faded text-sm font-normal whitespace-nowrap">
                  {t(col.labelKey)}
                </TextMedium>
                {col.tooltipKey && (
                  <CountryConfigsTooltip content={t(col.tooltipKey)} delayMs={300}>
                    <span className="text-foreground_faded flex items-center cursor-default">
                      <Icon path={mdiInformationOutline} size="regular" />
                    </span>
                  </CountryConfigsTooltip>
                )}
              </div>
            ))}
          </div>

          {/* Rows */}
          {entries.map((entry, index) => {
            const rowErrors = errors[index] ?? {}
            const rowId = entry._rowId ?? String(index)
            return (
              <div
                key={index}
                className="flex border-b border-border"
                data-cy={`overbooking-entry-${index}`}
              >
                {/* Customer tier */}
                <div className="w-[133px] shrink-0 px-2 h-14 flex items-center">
                  <CountryConfigsTooltip content={touchedRows.has(rowId) ? (rowErrors.customerTier ?? '') : ''} delayMs={300}>
                    <div className="w-full" ref={index === entries.length - 1 ? newRowRef : undefined}>
                      <ControlledInput
                        value={entry.customerTier}
                        onChange={(e) => handleEntryChange(index, 'customerTier', e.target.value)}
                        onBlur={() => markTouched(rowId)}
                        isDisabled={isReadOnly}
                        variant="small"
                        error={touchedRows.has(rowId) ? rowErrors.customerTier : undefined}
                        isTouched={false}
                        data-cy={`overbooking-entry-${index}-customerTier`}
                        className="w-full"
                      />
                    </div>
                  </CountryConfigsTooltip>
                </div>

                {/* Overbooking % */}
                <div className="w-[148px] shrink-0 px-2 h-14 flex items-center">
                  <NumericalInput
                    value={entry.overbookingPercent}
                    onChange={(v) => handleEntryChange(index, 'overbookingPercent', v)}
                    min={0}
                    disabled={isReadOnly}
                    error={rowErrors.overbookingPercent}
                    data-cy={`overbooking-entry-${index}-percent`}
                  />
                </div>

                {/* Lead time value */}
                <div className="w-[140px] shrink-0 px-2 h-14 flex items-center">
                  <NumericalInput
                    value={entry.leadTimeValue}
                    onChange={(v) => handleEntryChange(index, 'leadTimeValue', v)}
                    min={0}
                    disabled={isReadOnly}
                    error={rowErrors.leadTimeValue}
                    data-cy={`overbooking-entry-${index}-leadTime`}
                  />
                </div>

                {/* Unit of measure */}
                <div className="w-[188px] shrink-0 px-2 h-14 flex items-center">
                  <FilterDropdown
                    value={entry.leadTimeUomId}
                    onChange={(v) => handleEntryChange(index, 'leadTimeUomId', v)}
                    options={uomOptions}
                    size="small"
                    disabled={true}
                    placeholder={t('countryConfigurations.overbookingAllowance.placeholder')}
                    data-cy={`overbooking-entry-${index}-uom`}
                  />
                </div>

                {/* Reference event */}
                <div className="w-[255px] shrink-0 px-2 h-14 flex items-center">
                  <FilterDropdown
                    value={entry.leadTimeReferenceEvent}
                    onChange={(v) => handleEntryChange(index, 'leadTimeReferenceEvent', v)}
                    options={referenceEventOptions}
                    size="medium"
                    disabled={isReadOnly}
                    placeholder={t('countryConfigurations.overbookingAllowance.placeholder')}
                    data-cy={`overbooking-entry-${index}-refEvent`}
                  />
                </div>

                {/* Actions (delete) */}
                <div className="w-[66px] shrink-0 px-2 h-14 flex items-center justify-center">
                  {!isReadOnly && (
                    <Button
                      variant="secondary"
                      square
                      outline
                      size="regular"
                      onClick={() => handleRemoveEntry(index)}
                      aria-label={`Delete entry ${index + 1}`}
                      data-cy={`overbooking-entry-${index}-delete`}
                    >
                      <Icon path={mdiTrashCanOutline} size="regular" />
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
